import { defineComponent, computed, Ref, nextTick, ref, ComponentInstance, useModel } from "vue";
import { addScopedGlobalListener, VueProp, OptionalVueProp } from "../../core/vueHelpers";
import Day, { DayClasses } from "./Day";
import { checkAndAdjustMonth } from "./MonthSelector";

const CalendarClasses =
{
    calendar: "datePickerCalendar",
    table: "datePickerCalendar__table",
    weeks: "datePickerCalendar__weeks",
    weeksHeader: "datePickerCalendar__weeksHeader",
    weeksHeaderText: "datePickerCalendar__weeksHeaderText"
};

export interface CalendarExposed
{
    focusCurrentDay(): void;
}

export default /*#__PURE__*/ defineComponent(
{
    name: "calendar",
    emits: 
    {
        "update:selectedDate": (_: Date) => true,
        "update:month": (_: number) => true,
        "update:year": (_: number) => true,
    },
    props: 
    {
        month: VueProp(Number),
        year: VueProp(Number),
        yearHigh: VueProp(Number),
        yearLow: VueProp(Number),
        selectedDate: OptionalVueProp<Date>(Date),
    },
    setup(props, context)
    {
        const month = useModel(props, "month");
        const year = useModel(props, "year");
        const calendar = ref<HTMLDivElement>();
        const selectedDate = useModel(props, "selectedDate") as Ref<Date>;
        const selectedDay = ref<ComponentInstance<typeof Day>>();
        const dates = computed(() =>
        {
            return getDatesForMonth(year.value, month.value);
        });

        context.expose(
        {
            focusCurrentDay() { selectedDay.value?.$el.focus(); }
        });

        addScopedGlobalListener(document.body, "keydown", handleKeydown);

        return () =>
        {
            const todaysDate = new Date();
            todaysDate.setHours(0, 0, 0, 0);
            let tabDate: Date;

            // Clone selectedDate.value to avoid mutating the original object
            const tempSelectedDate = new Date(selectedDate.value.getTime());
            tempSelectedDate.setHours(0);
            tempSelectedDate.setMinutes(0);
            tempSelectedDate.setSeconds(0);
            // Use the selected date, if there is one
            if (dates.value.some(d => d.valueOf() === tempSelectedDate.valueOf()))
            {
                tabDate = tempSelectedDate;
            }
            // Use today's date, if it falls in the current month
            else if (dates.value.some(d => d.valueOf() === todaysDate.valueOf()) && todaysDate.getFullYear() === year.value && todaysDate.getMonth() === month.value - 1)
            {
                tabDate = todaysDate;
            }
            // Otherwise, use the first of the month
            else
            {
                tabDate = new Date(year.value, month.value - 1, 1);
            }

            return <div class={CalendarClasses.calendar}>
                <table class={CalendarClasses.table} ref={calendar} role="grid">
                    <thead class={CalendarClasses.weeksHeader}>
                        <tr>
                            <th class={CalendarClasses.weeksHeaderText}><span aria-hidden="true">S</span><span class="sr-only">Sunday</span></th>
                            <th class={CalendarClasses.weeksHeaderText}><span aria-hidden="true">M</span><span class="sr-only">Monday</span></th>
                            <th class={CalendarClasses.weeksHeaderText}><span aria-hidden="true">T</span><span class="sr-only">Tuesday</span></th>
                            <th class={CalendarClasses.weeksHeaderText}><span aria-hidden="true">W</span><span class="sr-only">Wednesday</span></th>
                            <th class={CalendarClasses.weeksHeaderText}><span aria-hidden="true">T</span><span class="sr-only">Thursday</span></th>
                            <th class={CalendarClasses.weeksHeaderText}><span aria-hidden="true">F</span><span class="sr-only">Friday</span></th>
                            <th class={CalendarClasses.weeksHeaderText}><span aria-hidden="true">S</span><span class="sr-only">Saturday</span></th>
                        </tr>
                    </thead>
                    {Array.chunk(dates.value.map(date =>
                    {
                        const outsideMonth = date.getMonth() + 1 !== month.value;
                        const isSelected = date.getFullYear() === selectedDate.value.getFullYear()
                            && date.getMonth() === selectedDate.value.getMonth()
                            && date.getDate() === selectedDate.value.getDate();
                        const isToday = date.valueOf() === todaysDate.valueOf();
                        const isTabElement = tabDate.valueOf() === date.valueOf();
                        const tabIndex = isTabElement ? "0" : "-1";
                        const ref = isTabElement ? selectedDay : undefined;

                        return <Day
                            date={date}
                            outsideMonth={outsideMonth}
                            selected={isSelected}
                            handleClick={handleDayClick}
                            tabIndex={tabIndex}
                            isToday={isToday}
                            ref={ref} />;

                        function handleDayClick()
                        {
                            setDateFromSelected(date);
                            if (outsideMonth)
                            {
                                month.value = date.getMonth() + 1;
                                year.value = date.getFullYear();
                            }
                        }
                    }), 7).map(x => <tr role="row">{x}</tr>)}
                </table>
            </div>
        };

        async function handleKeydown(event: JQuery.KeyDownEvent)
        {
            const eventKeys = window.skyward.keys.getKeyNamesFromEvent(event);
            if (eventKeys.ENTER)
            {
                event.preventDefault();
                const element = event.target as HTMLElement;
                if (element && element.classList.contains(DayClasses.day))
                {
                    const date = new Date(element.dataset.date!);
                    if (date.isValidDate())
                    {
                        setDateFromSelected(date);
                        year.value = date.getFullYear();
                        month.value = date.getMonth() + 1;
                    }
                }
            }
            else if (!eventKeys.ALT && (eventKeys.ARROW_UP || eventKeys.ARROW_DOWN || eventKeys.ARROW_LEFT || eventKeys.ARROW_RIGHT))
            {
                event.preventDefault();
                const element = event.target as HTMLElement;
                if (element && element.classList.contains(DayClasses.dayContainer))
                {
                    const date = new Date(element.dataset.date!);
                    if (!date.isValidDate())
                    {
                        return;
                    }

                    if (eventKeys.ARROW_DOWN)
                    {
                        date.setDate(date.getDate() + 7);
                    }
                    else if (eventKeys.ARROW_UP)
                    {
                        date.setDate(date.getDate() - 7);
                    }
                    else if (eventKeys.ARROW_LEFT)
                    {
                        date.setDate(date.getDate() - 1);
                    }
                    else if (eventKeys.ARROW_RIGHT)
                    {
                        date.setDate(date.getDate() + 1);
                    }

                    const newISODate = date.toISOString();
                    if (calendar.value)
                    {
                        const matchingElement = calendar.value.querySelector(`[data-date="${newISODate}"]`) as HTMLElement;
                        if (matchingElement)
                        {
                            matchingElement.focus();
                            return;
                        }
                    }

                    // If we cannot find the element, determine how to adjust the Calendar
                    if (eventKeys.ARROW_DOWN || eventKeys.ARROW_RIGHT)
                    {
                        const { month: newMonth, year: newYear } = checkAndAdjustMonth(month.value, year.value, 1, props.yearHigh, props.yearLow);
                        month.value = newMonth;
                        year.value = newYear;
                    }
                    else if (eventKeys.ARROW_UP || eventKeys.ARROW_LEFT)
                    {
                        const { month: newMonth, year: newYear } = checkAndAdjustMonth(month.value, year.value, -1, props.yearHigh, props.yearLow);
                        month.value = newMonth;
                        year.value = newYear;
                    }

                    // Allow for the Calendar to render
                    await nextTick();

                    if (calendar.value)
                    {
                        const matchingDate = calendar.value.querySelector(`[data-date="${newISODate}"]`) as HTMLElement;
                        if (matchingDate)
                        {
                            matchingDate.focus();
                        }
                    }
                }
            }
        }

        function setDateFromSelected(newDate: Date)
        {
            const tempDate = newDate;
            tempDate.setHours(selectedDate.value.getHours());
            tempDate.setMinutes(selectedDate.value.getMinutes());
            tempDate.setSeconds(selectedDate.value.getSeconds());
            selectedDate.value = tempDate;
        }
    }
});

function getDatesForMonth(year: number, month: number)
{
    const dates = [];
    const firstDay = new Date(year, month - 1, 1);
    firstDay.setDate(firstDay.getDate() - firstDay.getDay());
    const lastDay = new Date(year, month, 0);
    lastDay.setDate(lastDay.getDate() + (6 - lastDay.getDay()));

    for (let currentDay = new Date(firstDay); currentDay <= lastDay; currentDay.setDate(currentDay.getDate() + 1))
    {
        dates.push(new Date(currentDay));
    }

    return dates;
}
